<template>
    <div class="audit-log p-3">
        <div class="flex items-center gap-3 mb-4 flex-wrap">
            <h2 class="text-xl m-0">Audit Log</h2>
            <Tag v-if="loaded && !canSeeAll" severity="info" value="Your entries only" />
            <span class="text-xs text-gray-500 ml-auto">
                Entries are kept for 365 days.
            </span>
        </div>

        <!-- Filters -->
        <div class="flex items-end gap-3 mb-3 flex-wrap">
            <div class="flex flex-col gap-1">
                <label class="text-xs text-gray-400">Date range</label>
                <DatePicker v-model="dateRange" selectionMode="range" :manualInput="false"
                    dateFormat="dd/mm/yy" showIcon placeholder="All time" class="w-64" />
            </div>

            <div class="flex flex-col gap-1">
                <label class="text-xs text-gray-400">Action</label>
                <Select v-model="filters.action" :options="actionOptions" optionLabel="label"
                    optionValue="value" placeholder="All actions" showClear class="w-56" filter />
            </div>

            <div v-if="canSeeAll" class="flex flex-col gap-1">
                <label class="text-xs text-gray-400">Who</label>
                <Select v-model="filters.actorId" :options="actorOptions" optionLabel="label"
                    optionValue="value" placeholder="Everyone" showClear class="w-64" filter />
            </div>

            <div class="flex flex-col gap-1">
                <label class="text-xs text-gray-400">Outcome</label>
                <Select v-model="filters.outcome" :options="outcomeOptions" optionLabel="label"
                    optionValue="value" placeholder="Any" showClear class="w-36" />
            </div>

            <div class="flex flex-col gap-1">
                <label class="text-xs text-gray-400">Search</label>
                <InputText v-model="searchInput" placeholder="Target, message or email"
                    class="w-64" @keyup.enter="applySearch" />
            </div>

            <div class="flex items-center gap-2 pb-1">
                <Checkbox v-model="filters.includeLogins" inputId="incLogins" binary />
                <label for="incLogins" class="text-sm cursor-pointer" title="Logins are the large majority of entries, so they're hidden by default">
                    Include logins
                </label>
            </div>

            <Button label="Reset" text size="small" @click="resetFilters" />
        </div>

        <ProgressSpinner v-if="loading" style="width: 40px; height: 40px" />

        <div v-else-if="!entries.length"
            class="text-center text-sm text-gray-400 py-10 border border-dashed border-gray-700 rounded">
            No audit entries match these filters.
        </div>

        <template v-else>
            <DataTable :value="entries" v-model:expandedRows="expandedRows" dataKey="_id"
                :scrollable="true" scrollHeight="65vh" class="text-sm">
                <Column expander :headerStyle="headerStyles" style="width: 3rem" />

                <Column field="createdAt" header="When" :headerStyle="headerStyles" style="width: 12rem">
                    <template #body="{ data }">
                        <span v-tooltip.top="new Date(data.createdAt).toISOString()">
                            {{ formatDateTime(data.createdAt) }}
                        </span>
                    </template>
                </Column>

                <Column header="Who" :headerStyle="headerStyles" style="width: 18rem">
                    <template #body="{ data }">
                        <div class="flex flex-col leading-tight">
                            <span>{{ data.actorEmail || '—' }}</span>
                            <span v-if="data.actorRole" class="text-xs text-gray-500">{{ data.actorRole }}</span>
                        </div>
                    </template>
                </Column>

                <Column field="action" header="Action" :headerStyle="headerStyles" style="width: 14rem">
                    <template #body="{ data }"><code>{{ data.action }}</code></template>
                </Column>

                <Column header="Target" :headerStyle="headerStyles">
                    <template #body="{ data }">
                        <div class="flex flex-col leading-tight">
                            <span v-if="targetOf(data).primary">{{ targetOf(data).primary }}</span>
                            <span v-else class="text-gray-600">—</span>
                            <span v-if="targetOf(data).secondary" class="text-xs text-gray-500">
                                {{ targetOf(data).secondary }}
                            </span>
                        </div>
                    </template>
                </Column>

                <Column field="outcome" header="Outcome" :headerStyle="headerStyles" style="width: 8rem">
                    <template #body="{ data }">
                        <Tag :severity="data.outcome === 'failed' ? 'danger' : 'success'" :value="data.outcome" />
                    </template>
                </Column>

                <template #expansion="{ data }">
                    <div class="p-3 text-sm expansion">
                        <p v-if="data.message" class="mb-3">{{ data.message }}</p>

                        <div v-if="data.changes && Object.keys(data.changes).length" class="mb-3">
                            <div class="text-xs text-gray-400 mb-1">Changes</div>
                            <table class="changes-table">
                                <thead>
                                    <tr><th>Field</th><th>Before</th><th>After</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(diff, field) in data.changes" :key="field">
                                        <td><code>{{ field }}</code></td>
                                        <td class="before">{{ display(diff.before) }}</td>
                                        <td class="after">{{ display(diff.after) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-if="data.meta && Object.keys(data.meta).length" class="mb-3">
                            <div class="text-xs text-gray-400 mb-1">Details</div>
                            <pre class="meta">{{ JSON.stringify(data.meta, null, 2) }}</pre>
                        </div>

                        <div class="text-xs text-gray-500">
                            <span v-if="data.ip">IP {{ data.ip }}</span>
                            <span v-if="data.userAgent" class="ml-3">{{ data.userAgent }}</span>
                        </div>
                    </div>
                </template>
            </DataTable>

            <Paginator :rows="limit" :totalRecords="total" :first="(page - 1) * limit"
                :rowsPerPageOptions="[25, 50, 100, 200]" class="mt-2"
                @page="onPage" />
        </template>
    </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Select from 'primevue/select';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Paginator from 'primevue/paginator';
import ProgressSpinner from 'primevue/progressspinner';
import AuditLogService from '@/services/AuditLogService';
import { headerStyles } from '@/global/headerStyles';

const loading = ref(false);
const loaded = ref(false);
const entries = ref([]);
const expandedRows = ref([]);
const page = ref(1);
const limit = ref(50);
const total = ref(0);

// Server-decided: whether this admin sees the whole trail or only their own
// entries. Never inferred client-side — the API is the authority, the UI just
// hides the actor filter when it would be meaningless.
const canSeeAll = ref(false);

const dateRange = ref(null);
const searchInput = ref('');
const filters = reactive({
    action: null,
    actorId: null,
    outcome: null,
    q: '',
    includeLogins: false,
});

const outcomeOptions = [
    { label: 'Success', value: 'ok' },
    { label: 'Failed', value: 'failed' },
];

const actionOptions = ref([]);
const actorOptions = ref([]);

const from = computed(() => toYMD(dateRange.value?.[0]));
const to = computed(() => toYMD(dateRange.value?.[1]));

async function fetchEntries() {
    loading.value = true;
    try {
        const { data } = await AuditLogService.getAuditLogs({
            page: page.value,
            limit: limit.value,
            action: filters.action || undefined,
            actorId: filters.actorId || undefined,
            outcome: filters.outcome || undefined,
            from: from.value,
            to: to.value,
            q: filters.q || undefined,
            includeLogins: filters.includeLogins ? 'true' : undefined,
        });
        entries.value = data.entries || [];
        total.value = data.total || 0;
        canSeeAll.value = !!data.canSeeAll;
    } catch (err) {
        console.error('Failed to load audit logs', err);
        entries.value = [];
        total.value = 0;
    } finally {
        loading.value = false;
        loaded.value = true;
    }
}

async function fetchFilterOptions() {
    try {
        const [actionsRes, actorsRes] = await Promise.all([
            AuditLogService.getAuditActions(),
            AuditLogService.getAuditActors(),
        ]);
        actionOptions.value = (actionsRes.data.actions || []).map(a => ({ label: a, value: a }));
        // Restricted admins get an empty actors list from the server by design.
        actorOptions.value = (actorsRes.data.actors || [])
            .filter(a => a.actorId)
            .map(a => ({ label: `${a.actorEmail} (${a.count})`, value: a.actorId }));
    } catch (err) {
        console.error('Failed to load audit filter options', err);
    }
}

function applySearch() {
    filters.q = searchInput.value.trim();
}

function resetFilters() {
    dateRange.value = null;
    searchInput.value = '';
    filters.action = null;
    filters.actorId = null;
    filters.outcome = null;
    filters.q = '';
    filters.includeLogins = false;
}

function onPage(e) {
    page.value = e.page + 1;
    limit.value = e.rows;
}

// Any filter change resets to page 1 — otherwise you can land on page 7 of a
// result set that now only has 2 pages and see an empty table.
watch(filters, () => { page.value = 1; fetchEntries(); }, { deep: true });
watch(dateRange, () => {
    // selectionMode="range" fires once with [start, null] mid-selection; only
    // refetch when the range is complete or fully cleared.
    if (dateRange.value && !dateRange.value[1]) return;
    page.value = 1;
    fetchEntries();
});
watch([page, limit], fetchEntries);

onMounted(() => {
    fetchEntries();
    fetchFilterOptions();
});

function toYMD(d) {
    if (!d) return undefined;
    // Local Y-M-D, NOT toISOString() — that shifts to UTC and silently moves
    // the day back for anyone east of Greenwich.
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function formatDateTime(v) {
    if (!v) return '—';
    return new Date(v).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

function display(v) {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    if (v === '') return '(empty)';
    return String(v);
}

// A bare 24-hex ObjectId. Showing one to a human conveys nothing, so these get
// suppressed or trimmed rather than printed in full.
const OBJECT_ID = /^[a-f0-9]{24}$/i;
const shortId = id => (OBJECT_ID.test(id || '') ? `${String(id).slice(0, 8)}…` : id);

/**
 * Build the Target cell.
 *
 * Prefers the structured meta the server now snapshots (advertiser, publisher,
 * market, deals) over target.label, because label is a single flat string and
 * older entries wrote raw ids into it — "advertiser=6a84… site=686f… market=GB"
 * and "SBL 6a68… on 2026-08-17T22:00:00.000Z". Where meta has names we render
 * those; where it doesn't we salvage what we can from the label instead of
 * printing the id soup.
 *
 * Returns { primary, secondary } — secondary renders small and dim.
 */
function targetOf(row) {
    const m = row?.meta || {};
    const label = row?.target?.label || '';

    // --- preferred: structured meta ---
    if (m.advertiser || m.publisher) {
        const primary = [
            m.advertiser || 'Unknown advertiser',
            m.publisher ? `— ${m.publisher}` : '',
            m.market ? `(${m.market})` : '',
        ].filter(Boolean).join(' ');

        const bits = [];
        if (m.site) bits.push(m.site);
        if (m.dealType) bits.push(m.dealType);
        if (m.theirDeal) bits.push(`theirs ${m.theirDeal}`);
        if (m.ourDeal) bits.push(`ours ${m.ourDeal}`);
        // The adjusted day matters more than the deal terms on an adjustment.
        if (row.action === 'earnings.adjustment' && m.date) {
            bits.unshift(formatDay(m.date));
        }
        return { primary, secondary: bits.join(' · ') || null };
    }

    // --- fallback: an older row, whose label may be raw ids ---
    // "advertiser=<id> site=<id> market=GB" — keep only what reads as English.
    const kv = label.match(/market=([A-Za-z]{2,})/);
    if (label.includes('advertiser=') && kv) {
        return { primary: `Site link (${kv[1].toUpperCase()})`, secondary: 'ids only — predates enrichment' };
    }
    // "SBL <id> on <iso>" — the date is the useful half.
    const sbl = label.match(/^SBL\s+([a-f0-9]{24})\s+on\s+(.+)$/i);
    if (sbl) {
        return { primary: `Adjustment · ${formatDay(sbl[2])}`, secondary: `SBL ${shortId(sbl[1])} — predates enrichment` };
    }

    if (label) return { primary: label, secondary: row?.target?.kind || null };
    if (row?.target?.id) return { primary: shortId(row.target.id), secondary: row?.target?.kind || null };
    return { primary: null, secondary: null };
}

function formatDay(v) {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
    // UTC: stored adjustment dates are whole days at UTC midnight, so reading
    // them in local time would shift the label back a day for UTC- viewers.
    return d.toLocaleDateString('en-GB', {
        timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric',
    });
}
</script>

<style scoped>
.changes-table {
    border-collapse: collapse;
    font-size: 0.8rem;
}
.changes-table th,
.changes-table td {
    border: 1px solid var(--p-content-border-color, #3f3f46);
    padding: 0.25rem 0.6rem;
    text-align: left;
    vertical-align: top;
    max-width: 28rem;
    word-break: break-word;
}
.changes-table th {
    font-weight: 500;
    opacity: 0.7;
}
.changes-table .before { opacity: 0.65; }
.changes-table .after { color: #86efac; }

.meta {
    font-size: 0.75rem;
    margin: 0;
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.04);
    max-height: 18rem;
    overflow: auto;
}
</style>
